import { Text } from "react-native";
import { useTranslation } from "react-i18next";
import { muscleName, programRoundLabel, type SimulatedMuscle } from "@heraion/shared";

import { joinList } from "../joinList";
import { CoverageRowShell } from "../primitives/CoverageRowShell";
import {
  LANDMARK_ZONE_GLYPH,
  NO_BAND_GLYPH,
  landmarkZoneWord,
  noBandWord,
} from "../primitives/LandmarkBar";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import type { TFn } from "../i18nTypes";
import type { UiTheme } from "../theme";
import { formatSets } from "./simulationShared";
import type { SimulationStyles } from "./simulationStyles";

type Lang = Parameters<typeof muscleName>[1];

/** Where the cycle's average leaves the muscle, in words: a direction, never
 *  a promise; above MRV costs more than it returns, it does not lose
 *  (measurement-contract §8ter.3). */
const directionWord = (t: TFn, m: SimulatedMuscle): string | null =>
  m.direction === "unlikely-to-grow"
    ? t("simulator.direction.unlikelyToGrow", { defaultValue: "likely too little to grow" })
    : m.direction === "likely-to-grow"
      ? t("simulator.direction.likelyToGrow", { defaultValue: "in the range where muscles grow" })
      : m.direction === "diminishing-returns"
        ? t("simulator.direction.diminishingReturns", { defaultValue: "more sets, little more return" })
        : null;

// #355: one muscle of the simulator, the shape of Progress's compact coverage
// row (MuscleCoverageCompactRow) without a «today»: the cycle's average
// against the band, its days a week, the rounds out of the band, and the sets
// counted hard by assumption. One accessible node, one sentence.
export function SimulationMuscleRow({
  theme,
  styles,
  muscle: m,
  days,
  lang,
  divider,
}: {
  theme: UiTheme;
  styles: SimulationStyles;
  muscle: SimulatedMuscle;
  /** Days a week the muscle gets a hard set as a substantial mover. */
  days: number;
  lang: Lang;
  divider: boolean;
}) {
  const { t } = useTranslation();
  const name = muscleName(m.muscle, lang);
  const sets = formatSets(m.setsPerWeek, lang);
  // Sets but no day: the credit comes only from exercises where the muscle is
  // a secondary mover (under the substantial threshold), so «0 days» would
  // contradict the sets beside it.
  const indirect = days === 0 && m.setsPerWeek > 0;
  const value = indirect
    ? t("simulator.perWeekIndirect", { sets, defaultValue: "{{sets}} / wk · indirect only" })
    : t("simulator.perWeekDays", {
        sets,
        count: days,
        days: formatSets(days, lang),
        defaultValue: "{{sets}} / wk · {{days}} days",
      });
  const valueSpoken = indirect
    ? t("simulator.perWeekIndirectA11y", {
        sets,
        defaultValue: "{{sets}} hard sets a week, all of them indirect",
      })
    : t("simulator.perWeekDaysA11y", {
        sets,
        days: formatSets(days, lang),
        defaultValue: "{{sets}} hard sets a week, on {{days}} days",
      });
  const direction = directionWord(t, m);
  const zone =
    m.band === undefined || m.zone === undefined
      ? `${NO_BAND_GLYPH} ${noBandWord(t)}`
      : `${LANDMARK_ZONE_GLYPH[m.zone]} ${landmarkZoneWord(t, m.zone)}${direction ? `: ${direction}` : ""}`;
  const weeks = (ws: SimulatedMuscle["weeksBelowMev"]) =>
    joinList(
      ws.map((w) => t("mobile.plan.programWeekShort", { n: w.weekIdx + 1, defaultValue: programRoundLabel(w.weekIdx + 1) })),
      lang,
    );
  const below =
    m.weeksBelowMev.length > 0
      ? `${LANDMARK_ZONE_GLYPH["below-mev"]} ${t("simulator.weeksBelow", { weeks: weeks(m.weeksBelowMev), defaultValue: "below MEV in {{weeks}}" })}`
      : null;
  const above =
    m.weeksAboveMrv.length > 0
      ? `${LANDMARK_ZONE_GLYPH["above-mrv"]} ${t("simulator.weeksAbove", { weeks: weeks(m.weeksAboveMrv), defaultValue: "above MRV in {{weeks}}" })}`
      : null;
  const assumed =
    m.assumedSetsPerWeek > 0
      ? t("simulator.assumed", {
          n: formatSets(m.assumedSetsPerWeek, lang),
          defaultValue: "{{n}} of them with no RPE or RIR, counted hard",
        })
      : null;
  const lines = [below, above, assumed].filter((x): x is string => x !== null);
  return (
    <CoverageRowShell
      theme={theme}
      name={name}
      value={value}
      spoken={[name, valueSpoken, zone, ...lines].join(", ")}
      bar={m.band && m.zone ? { weeklySets: m.setsPerWeek, band: m.band, zone: m.zone } : undefined}
      divider={divider}
    >
      <Text style={styles.rowZone} {...hiddenFromScreenReader()}>
        {zone}
      </Text>
      {lines.map((l) => (
        <Text key={l} style={styles.quiet} {...hiddenFromScreenReader()}>
          {l}
        </Text>
      ))}
    </CoverageRowShell>
  );
}
